import { useEffect, useRef, useState } from 'react';
import { Users, BookOpen, Clock, Briefcase } from 'lucide-react';

/* ── useInView ── */
function useInView(opts = {}) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVis(true); obs.unobserve(el); }
    }, { threshold: 0.2, ...opts });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}

/* ── Animated counter ── */
function useCounter(target, start, dur = 2000) {
  const [c, setC] = useState(0);
  useEffect(() => {
    if (!start || !target) return;
    let s, raf;
    const go = (t) => {
      if (!s) s = t;
      const p = Math.min((t - s) / dur, 1);
      setC(Math.floor((1 - Math.pow(1 - p, 4)) * target));
      if (p < 1) raf = requestAnimationFrame(go); else setC(target);
    };
    raf = requestAnimationFrame(go);
    return () => cancelAnimationFrame(raf);
  }, [target, start]);
  return c;
}

const STATS = [
  { key: 'students',  icon: Users,     label: 'طلاب مسجلين',    value: 140, suffix: '+' },
  { key: 'courses',   icon: BookOpen,  label: 'دورات متاحة',    value: 5,   suffix: ''  },
  { key: 'hours',     icon: Clock,     label: 'ساعات تعليمية',  value: 120, suffix: '+' },
  { key: 'projects',  icon: Briefcase, label: 'مشاريع منجزة',   value: 350, suffix: '+' },
];

export default function HomeStats() {
  const [ref, isVisible] = useInView();

// Fetch real data from Firestore
  const [data, setData] = useState({ students: 140, courses: 5, hours: 120, projects: 350 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { collection, getDocs, query, where, getCountFromServer } = await import('firebase/firestore');
        const { db } = await import('../../firebase');

        // Get users count
        const usersSnap = await getCountFromServer(collection(db, 'users'));
        const studentsCount = usersSnap.data().count;

        // Get courses and calculate hours
        const coursesQ = query(collection(db, 'courses'), where('status', '==', 'published'));
        const coursesDocs = await getDocs(coursesQ);
        const coursesCount = coursesDocs.size;

        // Calculate hours based on durationLabel or similar if it exists, otherwise use a placeholder calculation
        let totalHours = 120; // Default
        if (coursesCount > 0) {
           totalHours = coursesCount * 24; // Approximation or you can parse courses if they have numeric hours.
        }

        setData({
          students: studentsCount > 140 ? studentsCount : 140,
          courses: coursesCount > 5 ? coursesCount : 5,
          hours: totalHours > 120 ? totalHours : 120,
          projects: 350 // Assuming projects count is manually updated or calculated differently
        });
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };
    fetchStats();
  }, []);

  const students = useCounter(data.students, isVisible);
  const courses  = useCounter(data.courses,  isVisible);
  const hours    = useCounter(data.hours,    isVisible);
  const projects = useCounter(data.projects, isVisible);
  const counts = { students, courses, hours, projects };

  return (
    <section ref={ref} className="py-12 sm:py-16 relative bg-slate-50 dark:bg-black transition-colors duration-500 border-b border-t border-slate-200 dark:border-white/5 z-20 overflow-hidden">
      {/* Cube-pattern background — same as CourseStats */}
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 dark:opacity-[0.02] mix-blend-overlay pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 tracking-wide max-w-7xl mx-auto">
          {STATS.map((stat, idx) => {
            const Icon = stat.icon;
            const val = counts[stat.key];
            return (
              <div
                key={stat.key}
                className="bg-white/70 dark:bg-zinc-900/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-2xl sm:rounded-3xl p-5 sm:p-8 hover:-translate-y-2 transition-all duration-500 group relative overflow-hidden shadow-sm hover:shadow-xl dark:shadow-[0_10px_30px_rgba(0,0,0,0.3)] hover:border-primary-500/30 dark:hover:border-primary-500/50 flex flex-col pt-7 sm:pt-10"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? undefined : 'translateY(30px)',
                  transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${idx * 0.12}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${idx * 0.12}s`,
                }}
              >
                {/* Decorative glow — top-right */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500/5 dark:bg-primary-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700 ease-out blur-[20px] pointer-events-none" />

                {/* Icon Box */}
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-slate-50 dark:bg-zinc-800/80 border border-slate-200 dark:border-white/10 flex items-center justify-center mb-4 sm:mb-6 relative group-hover:scale-110 transition-transform duration-500 z-10 shadow-sm dark:shadow-none">
                  <div className="absolute inset-0 bg-primary-500/30 dark:bg-primary-500/40 blur-[12px] rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative z-10">
                    <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600 dark:text-primary-500" />
                  </div>
                </div>

                {/* Counter */}
                <h3 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white mb-2 sm:mb-3 transition-colors duration-500 relative z-10 leading-tight tabular-nums" dir="ltr">
                  {val}{stat.suffix}
                </h3>

                {/* Label */}
                <p className="text-sm sm:text-base md:text-xl text-slate-600 dark:text-zinc-400 font-medium leading-relaxed transition-colors duration-500 relative z-10">
                  {stat.label}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
